import type { LocaleInfo, RoundResult, SessionResponse } from './protocol';

/**
 * The REST client. Everything that happens outside a round goes through here;
 * the round itself is on the WebSocket.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Where the bearer token is kept.
 *
 * Two keys, because the same browser on the same origin can hold two different
 * kinds of session: our own player's, and an operator's player inside an iframe
 * on their casino. They are different accounts and neither should be able to
 * evict the other -- a player who opens the game from a casino must not find
 * themselves signed out of our own site afterwards.
 *
 * Which one applies is decided by the page, not by a flag: the launch URL is
 * /play, and only a B2B launch is served there.
 */
const TOKEN_KEY = 'crashgame.token';
const B2B_TOKEN_KEY = 'crashgame.b2b.token';
const LAUNCH_CONTEXT_KEY = 'crashgame.b2b.launch';

export function isOperatorMode(): boolean {
  return location.pathname === '/play' || location.pathname.startsWith('/play/');
}

function tokenKey(): string {
  return isOperatorMode() ? B2B_TOKEN_KEY : TOKEN_KEY;
}

export function storedToken(): string | null {
  try {
    return localStorage.getItem(tokenKey());
  } catch {
    // Private browsing modes can throw on access rather than return null.
    return null;
  }
}

export function storeToken(token: string | null): void {
  try {
    if (token === null) {
      localStorage.removeItem(tokenKey());
      localStorage.removeItem(LAUNCH_CONTEXT_KEY);
    } else localStorage.setItem(tokenKey(), token);
  } catch {
    /* nothing useful to do; the session simply will not survive a reload */
  }
}

/**
 * What the launch told us, kept for as long as the session it belongs to.
 *
 * The launch token is single-use, so a reload cannot ask again -- and the lobby
 * link has to survive a reload, or the way out of the game disappears the first
 * time a player refreshes. It lives next to the token, under the same key
 * scheme, and is cleared with it.
 */
export interface LaunchContext {
  returnUrl?: string;
  platform?: string;
}

export function storeLaunchContext(context: LaunchContext): void {
  try {
    localStorage.setItem(LAUNCH_CONTEXT_KEY, JSON.stringify(context));
  } catch {
    /* the lobby link will simply be absent after a reload */
  }
}

export function launchContext(): LaunchContext {
  try {
    const raw = localStorage.getItem(LAUNCH_CONTEXT_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const { returnUrl, platform } = parsed as LaunchContext;
    return {
      ...(typeof returnUrl === 'string' ? { returnUrl } : {}),
      ...(typeof platform === 'string' ? { platform } : {}),
    };
  } catch {
    return {};
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const token = storedToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const text = await response.text();
  const parsed: unknown = text ? safeParse(text) : null;

  if (!response.ok) {
    const err = (parsed as { error?: { code?: string; message?: string } })?.error;
    throw new ApiError(
      response.status,
      err?.code ?? 'http_error',
      err?.message ?? `Request failed with status ${response.status}`,
      parsed,
    );
  }

  return parsed as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export interface RegisterInput {
  username: string;
  email: string;
  password: string;
  currency: string;
  countryCode: string;
  dateOfBirth: string;
  acceptTerms: boolean;
  /**
   * The language the form was filled in in, stored as the account's preference.
   * Separate from `currency` on purpose: a player reading Turkish may settle in
   * USD, and inferring either from the other is how they end up shown a wallet
   * they do not hold.
   */
  language: string;
}

/**
 * The `pool` query parameter, or nothing at all when the caller has no series to
 * name.
 *
 * Encoded rather than interpolated: the code is server-generated and matches
 * `[a-z0-9-]+`, but a value that reaches a URL from a message off the wire is
 * encoded on principle, not on inspection.
 */
function poolParam(pool: string, separator: '?' | '&'): string {
  return pool ? `${separator}pool=${encodeURIComponent(pool)}` : '';
}

export const api = {
  currencies: () =>
    request<{ currencies: Array<{ code: string; name: string; scale: number; isCrypto: boolean; minBet: string; maxBet: string }> }>(
      'GET',
      '/api/v1/currencies',
    ),

  /**
   * The offered languages and the one this request resolves to. Public, because
   * the sign-in screen has to be readable before there is a session.
   */
  languages: () => request<LocaleInfo>('GET', '/api/v1/languages'),

  /** Stores the account's language. The empty string returns it to detection. */
  setLanguage: (language: string) => request<LocaleInfo>('PUT', '/api/v1/me/language', { language }),

  register: (input: RegisterInput) => request<{ userId: string }>('POST', '/api/v1/auth/register', input),

  login: async (identifier: string, password: string) => {
    const result = await request<{ token: string; expiresAt: number }>('POST', '/api/v1/auth/login', {
      identifier,
      password,
    });
    storeToken(result.token);
    return result;
  },

  logout: async () => {
    try {
      await request<void>('POST', '/api/v1/auth/logout');
    } finally {
      storeToken(null);
    }
  },

  /**
   * The handshake. This is the only call that decides whether the game screen
   * opens: the currency, the balance and the right to bet all come from here and
   * are never assumed locally.
   */
  session: () => request<SessionResponse>('POST', '/api/v1/game/session'),

  /**
   * Trades the single-use launch token in the iframe URL for a session.
   *
   * Single-use is the whole point of it (b2b.md §A.7), which is why the client
   * only calls this when it has no working session of its own: on a reload the
   * URL still carries a token that is already spent, and the session it produced
   * the first time is the one to keep using.
   */
  /**
   * Trades the single-use launch token for a session.
   *
   * `returnUrl` and `platform` are launch-scoped rather than account-scoped -- the
   * same player may be launched into a mobile app now and a desktop lobby
   * tomorrow -- so they arrive here, once, and the caller keeps them for the life
   * of the session. See storeLaunchContext.
   */
  b2bSession: (token: string) =>
    request<{
      token: string;
      expiresAt: number;
      operator?: { code: string; name: string };
      returnUrl?: string;
      platform?: string;
    }>('POST', '/api/v1/b2b/session', { token }),

  /**
   * Refills a fun-mode player's play balance. Refused for every other kind of
   * account, server-side: the button is only shown to a demo session, and the
   * endpoint does not trust that.
   */
  demoTopUp: () => request<{ balance: string; currency: string }>('POST', '/api/v1/demo/topup'),

  /**
   * The public results feed. The server accepts a limit of 1..200, so the caller
   * says how many it can show rather than the client hard-coding one number for
   * the strip and the history panel both.
   *
   * `pool` is the round series to read, and comes from the snapshot. Omitting it
   * asks for the shared series -- the public game -- which is where every player
   * without an operator rate of their own plays. Passing the wrong one is not a
   * privacy problem but it is a wrong answer: round numbers are counted inside a
   * series, so #412 is a different round in each.
   */
  recentResults: (limit = 30, pool = '') =>
    request<{ pool: string; results: RoundResult[] }>(
      'GET',
      `/api/v1/fair/recent?limit=${limit}${poolParam(pool, '&')}`,
    ),

  /** One settled round, with the seed that proves it. Unauthenticated. */
  fairRound: (nonce: number, pool = '') =>
    request<{ pool: string; verified: boolean; result: RoundResult }>(
      'GET',
      `/api/v1/fair/round/${nonce}${poolParam(pool, '?')}`,
    ),

  statement: () =>
    request<{ transactions: Array<{ at: string; type: string; amount: string; balance: string; currency: string; roundNonce?: number; multiplierX100?: number }> }>(
      'GET',
      '/api/v1/me/history?limit=50',
    ),

  /** Stores the chosen avatar. The server refuses anything outside 0..19. */
  setAvatar: (avatarId: number) =>
    request<{ avatarId: number }>('PUT', '/api/v1/me/avatar', { avatarId }),

  /**
   * Stores both audio switches together. Both are sent every time: a partial
   * update would let a lost request leave the account holding one old value.
   */
  setPreferences: (sound: boolean, music: boolean) =>
    request<{ sound: boolean; music: boolean }>('PUT', '/api/v1/me/preferences', { sound, music }),

  setLimits: (input: Record<string, unknown>) => request<{ ok: boolean; deferred: boolean; message: string }>('PUT', '/api/v1/me/limits', input),

  selfExclude: (input: { days?: number; permanent?: boolean; reason?: string; confirm: string }) =>
    request<{ ok: boolean }>('POST', '/api/v1/me/self-exclusion', input),

  // Development only; the server does not route these in production.
  devDeposit: (amount: string) => request<{ balance: string; currency: string }>('POST', '/api/v1/dev/deposit', { amount }),
  devVerifyKyc: () => request<{ account: unknown }>('POST', '/api/v1/dev/verify-kyc', {}),
};
